import React, { useEffect, useState } from "react";
import config from "../config.js";
import { detailToString } from '../utils/apiError';

/**
 * ComponentAckPortal — PUBLIC vendor-facing page at /component-ack/<token>.
 *
 * No site auth and no app chrome: the 256-bit token in the URL is the only
 * credential, and the /portal/component-ack/* API routes are API-key-exempt for
 * exactly that reason. What a vendor sees here is deliberately narrow — the
 * item, the revision, what changed, and the drawings. No pricing, no stock
 * levels, nothing about any other customer's work.
 *
 * Sibling of RevisionApprovalPortal (which faces CUSTOMERS, about parts we
 * make). This one faces VENDORS, about parts they make for us — so the verb is
 * "acknowledge", not "approve": we are telling them what to build, not asking
 * permission.
 */
export default function ComponentAckPortal() {
  const API_BASE = config.API_BASE_URL;
  const token = window.location.pathname.split("/component-ack/")[1] || "";

  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [comments, setComments] = useState("");
  const [attested, setAttested] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/portal/component-ack/${encodeURIComponent(token)}`
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(detailToString(body.detail) || `HTTP ${res.status}`);
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setLoadErr(String(e.message || e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, token]);

  async function respond(action) {
    if (!name.trim()) {
      alert("Please enter your name.");
      return;
    }
    if (action === "acknowledge" && !attested) {
      alert("Please tick the confirmation box.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(
        `${API_BASE}/portal/component-ack/${encodeURIComponent(token)}/respond`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action,
            name: name.trim(),
            title: title.trim() || null,
            comments: comments.trim() || null,
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(body.detail) || `HTTP ${res.status}`);
      setResult(body);
    } catch (e) {
      alert(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  const shell = (children) => (
    <div style={{ minHeight: "100vh", background: "#f2f5f6", padding: "32px 16px" }}>
      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
          background: "#fff",
          border: "1px solid #cbd5d9",
          borderRadius: 6,
          padding: "28px 32px",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          color: "#131c24",
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "#6f808a" }}>
          KanCraft MFG
        </div>
        {children}
      </div>
    </div>
  );

  if (loadErr) {
    return shell(
      <>
        <h1 style={{ fontSize: 24, marginTop: 8 }}>This link isn't valid</h1>
        <p style={{ color: "#47565f" }}>
          {loadErr}. Ask your contact at KanCraft for a new link.
        </p>
      </>
    );
  }

  if (!data) {
    return shell(<p style={{ color: "#6f808a", marginTop: 16 }}>Loading…</p>);
  }

  if (data.expired) {
    return shell(
      <>
        <h1 style={{ fontSize: 24, marginTop: 8 }}>This link has expired</h1>
        <p style={{ color: "#47565f" }}>
          Ask your contact at KanCraft to send a new one — nothing has been lost.
        </p>
      </>
    );
  }

  const done = result || (data.already_acknowledged ? { status: "acknowledged" } : null);

  return shell(
    <>
      <h1 style={{ fontSize: 26, margin: "8px 0 4px" }}>
        {data.product_id} — Revision {data.revision_code}
      </h1>
      <p style={{ color: "#47565f", marginTop: 0 }}>{data.description}</p>

      {done ? (
        <div
          style={{
            background: done.status === "acknowledged" ? "#e1efe7" : "#f8eedc",
            border: "1px solid #cbd5d9",
            borderLeft: `4px solid ${done.status === "acknowledged" ? "#2a6349" : "#8a5606"}`,
            padding: "14px 18px",
            margin: "22px 0",
          }}
        >
          <strong>
            {done.status === "acknowledged"
              ? "Acknowledged — thank you."
              : "Your comments have been sent to KanCraft."}
          </strong>
          <div style={{ marginTop: 6, color: "#47565f" }}>
            {result?.message ||
              (data.acknowledged_by
                ? `Recorded for ${data.acknowledged_by}.`
                : "Nothing further is needed from you.")}
          </div>
        </div>
      ) : (
        <div
          style={{
            background: "#e1eef3",
            border: "1px solid #cbd5d9",
            borderLeft: "4px solid #0d5c79",
            padding: "14px 18px",
            margin: "22px 0",
          }}
        >
          We've changed this item. Please confirm you'll build to this revision.
        </div>
      )}

      <h2 style={{ fontSize: 16, marginBottom: 6 }}>Why it changed</h2>
      <p style={{ color: "#47565f" }}>{data.change_description || "Not stated."}</p>

      <h2 style={{ fontSize: 16, marginBottom: 6 }}>Effect on pieces already built</h2>
      <p style={{ color: "#47565f" }}>
        {
          {
            clarification: "None — this is a wording or drawing cleanup.",
            interchangeable:
              "Pieces built to the previous revision are still usable.",
            breaking:
              "Pieces built to the previous revision are NOT usable. Please contact us before making more.",
          }[data.stock_impact] || data.stock_impact
        }
      </p>

      {data.changes?.length > 0 && (
        <>
          <h2 style={{ fontSize: 16, margin: "22px 0 6px" }}>
            What changed
            {data.previous_revision_code
              ? ` (Rev ${data.previous_revision_code} → Rev ${data.revision_code})`
              : ""}
          </h2>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
              <thead>
                <tr>
                  {["Field", "Was", "Now"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "8px 10px",
                        borderBottom: "1px solid #cbd5d9",
                        background: "#e8edef",
                        fontSize: 11,
                        letterSpacing: ".1em",
                        textTransform: "uppercase",
                        color: "#6f808a",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.changes.map((c, i) => (
                  <tr key={i}>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #dde4e7", fontWeight: 600 }}>
                      {c.field}
                    </td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #dde4e7", color: "#9e2f22" }}>
                      {c.was === null || c.was === "" ? "—" : String(c.was)}
                    </td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid #dde4e7", color: "#2a6349" }}>
                      {c.now === null || c.now === "" ? "—" : String(c.now)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data.documents?.length > 0 && (
        <>
          <h2 style={{ fontSize: 16, margin: "22px 0 6px" }}>Drawings for this revision</h2>
          <ul style={{ paddingLeft: 18 }}>
            {data.documents.map((d, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                <a href={d.download_url} target="_blank" rel="noreferrer">
                  {d.display_name}
                </a>{" "}
                <span style={{ color: "#6f808a", fontSize: 13 }}>({d.original_filename})</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {!done && (
        <div style={{ borderTop: "1px solid #cbd5d9", marginTop: 26, paddingTop: 22 }}>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>Your response</h2>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <label style={{ flex: "1 1 240px" }}>
              <div style={{ fontSize: 13, marginBottom: 4 }}>Your name *</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: "100%", padding: 8, border: "1px solid #cbd5d9", borderRadius: 4 }}
              />
            </label>
            <label style={{ flex: "1 1 240px" }}>
              <div style={{ fontSize: 13, marginBottom: 4 }}>Title</div>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{ width: "100%", padding: 8, border: "1px solid #cbd5d9", borderRadius: 4 }}
              />
            </label>
          </div>
          <label style={{ display: "block", marginBottom: 12 }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Comments</div>
            <textarea
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              style={{ width: "100%", padding: 8, border: "1px solid #cbd5d9", borderRadius: 4 }}
            />
          </label>
          <label style={{ display: "block", marginBottom: 16, color: "#47565f" }}>
            <input
              type="checkbox"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            I confirm we will build {data.product_id} to Revision {data.revision_code}.
          </label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={() => respond("acknowledge")}
              disabled={busy}
              style={{
                background: "#0d5c79",
                color: "#fff",
                border: 0,
                padding: "10px 22px",
                borderRadius: 4,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {busy ? "Sending…" : "Acknowledge"}
            </button>
            <button
              onClick={() => respond("request_changes")}
              disabled={busy}
              style={{
                background: "#fff",
                color: "#8a5606",
                border: "1px solid #8a5606",
                padding: "10px 22px",
                borderRadius: 4,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Request changes
            </button>
          </div>
        </div>
      )}
    </>
  );
}
